import React, { useMemo } from 'react'
import { Link, useParams, Navigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { MapPin, Clock, Phone, Navigation, Users, AlertTriangle, ArrowRight } from 'lucide-react'
import SEOHead from '@/components/SEO/SEOHead'
import PageHeader from '@/components/Common/PageHeader'
import { neighborhoods, findNeighborhood } from '@/data/neighborhoods'
import { contactInfo } from '@/data/contact'
import { toTelHref, toWhatsAppHref } from '@/utils/phone'
import {
  buildGraph,
  breadcrumbListData,
  neighborhoodServiceSchema,
  faqData
} from '@/data/structured-data'

/** İki koordinat arasındaki kuş uçuşu mesafe (km) — komşu mahalleleri bulmak için. */
const haversine = (aLat: number, aLon: number, bLat: number, bLon: number) => {
  const R = 6371
  const dLat = ((bLat - aLat) * Math.PI) / 180
  const dLon = ((bLon - aLon) * Math.PI) / 180
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((aLat * Math.PI) / 180) * Math.cos((bLat * Math.PI) / 180) * Math.sin(dLon / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(s))
}

const NeighborhoodDetail: React.FC = () => {
  const { slug } = useParams()
  const n = findNeighborhood(slug)

  // Komşu mahalleler koordinatlardan hesaplanır; her sayfada farklıdır.
  // Hook'lar koşulsuz çağrılmalı, bu yüzden erken return'den önce yer alır.
  const neighbors = useMemo(() => {
    if (!n) return []
    return neighborhoods
      .filter(o => o.slug !== n.slug)
      .map(o => ({ ...o, gap: haversine(n.lat, n.lon, o.lat, o.lon) }))
      .sort((a, b) => a.gap - b.gap)
      .slice(0, 4)
  }, [n])

  // Bilinmeyen mahalle: 404 yerine hub sayfasına yönlendirilir.
  if (!n) return <Navigate to="/karakopru-veteriner" replace />

  const telHref = toTelHref(contactInfo.phone)
  const waHref = toWhatsAppHref(contactInfo.whatsapp)
  const url = `/karakopru-veteriner/${n.slug}`
  const mapsHref = `https://www.google.com/maps/dir/?api=1&origin=${n.lat},${n.lon}&destination=${encodeURIComponent(contactInfo.address)}`

  const description =
    `${n.name} Mahallesi'nden kliniğimize ${n.distanceKm} km, araçla yaklaşık ${n.driveMinutes} dakika. ` +
    `Kedi ve köpek muayenesi, aşı, kısırlaştırma, röntgen ve 7/24 acil veteriner hizmeti.`

  return (
    <>
      <SEOHead
        title={`${n.name} Veteriner | Karaköprü`}
        description={description}
        keywords={`${n.name.toLowerCase()} veteriner, ${n.name.toLowerCase()} veteriner kliniği, karaköprü ${n.name.toLowerCase()} veteriner, ${n.name.toLowerCase()} kedi veteriner, ${n.name.toLowerCase()} köpek veteriner, ${n.name.toLowerCase()} acil veteriner`}
        canonicalUrl={url}
        structuredData={buildGraph(
          neighborhoodServiceSchema({
            name: n.name,
            slug: n.slug,
            lat: n.lat,
            lon: n.lon,
            description,
            url
          }),
          faqData([
            {
              question: `${n.name} Mahallesi’nden kliniğe nasıl gelirim?`,
              answer:
                `${n.name} Mahallesi'nden kliniğimiz ${n.distanceKm} km mesafede ve araçla yaklaşık ` +
                `${n.driveMinutes} dakika sürüyor. Adresimiz: ${contactInfo.address}.`
            },
            {
              question: `${n.name} Mahallesi’nde gece açık veteriner var mı?`,
              answer:
                `Kliniğimiz 7/24 acil hizmet veriyor. ${n.name} Mahallesi'nden gece de gelebilirsiniz; ` +
                `yola çıkmadan önce ${contactInfo.phone} numarasını arayarak haber verin.`
            },
            {
              question: 'Kedi ve köpek dışında hayvan kabul ediyor musunuz?',
              answer:
                'Kliniğimiz ağırlıklı olarak kedi ve köpek sağlığı üzerine çalışmaktadır. ' +
                'Diğer türler için önce telefonla bilgi almanızı rica ederiz.'
            }
          ]),
          breadcrumbListData([
            { name: 'Ana Sayfa', url: '/' },
            { name: 'Karaköprü Veteriner', url: '/karakopru-veteriner' },
            { name: `${n.name} Mahallesi`, url }
          ])
        )}
      />

      <PageHeader
        title={`${n.name} Mahallesi Veteriner`}
        subtitle={`${n.name}’den kliniğimize ${n.distanceKm} km — araçla yaklaşık ${n.driveMinutes} dakika.`}
      />

      {/* Mahalleye özel gerçek veriler */}
      <section className="py-8 bg-white border-b">
        <div className="container-custom grid grid-cols-3 gap-4">
          {[
            { icon: Navigation, label: `${n.distanceKm} km`, sub: 'Klinik mesafesi' },
            { icon: Clock, label: `${n.driveMinutes} dk`, sub: 'Araçla süre' },
            { icon: Users, label: n.population.toLocaleString('tr-TR'), sub: 'Mahalle nüfusu' }
          ].map((s, i) => (
            <motion.div
              key={s.sub}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="text-center p-4 rounded-xl bg-gradient-to-br from-primary-50 to-accent-50 border border-primary-100"
            >
              <s.icon className="w-5 h-5 text-primary-600 mx-auto mb-2" />
              <div className="font-bold text-gray-900">{s.label}</div>
              <div className="text-xs text-gray-600 mt-1">{s.sub}</div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="section-padding">
        <div className="container-custom grid grid-cols-1 lg:grid-cols-3 gap-10">
          <article className="lg:col-span-2 space-y-10">
            <div>
              <h2 className="text-2xl font-bold mb-3">{n.name} Mahallesi’nden ulaşım</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                {n.name} Mahallesi, {n.population.toLocaleString('tr-TR')} kişilik nüfusuyla Karaköprü’nün
                yerleşik mahallelerinden biri. Kliniğimiz {n.name}’e {n.distanceKm} km mesafede,
                Batıkent Mahallesi Çardaklıkaya Bulvarı üzerinde, Fuar Alanı Kavşağı’nda bulunuyor.
                Normal trafikte yolculuk yaklaşık {n.driveMinutes} dakika sürüyor
                {n.driveMinutes <= 4 ? ' — acil bir durumda bu çok değerli bir süre.' : '.'}
              </p>
              <div className="flex flex-wrap gap-3">
                <a href={mapsHref} target="_blank" rel="noopener noreferrer" className="btn-primary inline-flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> Yol tarifi al
                </a>
                <a href={telHref} className="btn-secondary inline-flex items-center gap-2">
                  <Phone className="w-4 h-4" /> {contactInfo.phone}
                </a>
                <a href={waHref} target="_blank" rel="noopener noreferrer" className="btn-secondary">
                  WhatsApp
                </a>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold mb-3">{n.name}’de hangi hizmetleri veriyoruz?</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                {n.name} Mahallesi’nde yaşayan kedi ve köpek sahiplerinin bize en sık başvurduğu
                konular aşağıda. Hepsi için aynı klinikte, aynı gün içinde işlem yapılabiliyor.
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  { to: '/kedi', label: 'Kedi sağlığı', desc: 'Aşı, kısırlaştırma, iç-dış parazit' },
                  { to: '/kopek', label: 'Köpek sağlığı', desc: 'Aşı takvimi, kısırlaştırma, parazit' },
                  { to: '/acil-veteriner', label: 'Acil müdahale', desc: '7/24, gece ve tatil dahil' },
                  { to: '/rontgen-fiyatlari', label: 'Röntgen ve ultrason', desc: 'Dijital görüntüleme' }
                ].map(s => (
                  <Link
                    key={s.to}
                    to={s.to}
                    className="group p-4 rounded-xl border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all"
                  >
                    <div className="font-semibold text-gray-900 group-hover:text-primary-700">{s.label}</div>
                    <div className="text-sm text-gray-600 mt-1">{s.desc}</div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Komşu mahalleler — koordinatlardan hesaplanır */}
            <div>
              <h2 className="text-2xl font-bold mb-3">{n.name}’e komşu mahalleler</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                {n.name} Mahallesi’ne en yakın mahallelerden de aynı klinikte hizmet alabilirsiniz.
              </p>
              <div className="flex flex-wrap gap-2">
                {neighbors.map(o => (
                  <Link
                    key={o.slug}
                    to={`/karakopru-veteriner/${o.slug}`}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-gray-200 text-sm text-gray-700 hover:border-primary-300 hover:text-primary-700 hover:bg-primary-50 transition-colors"
                  >
                    {o.name}
                    <span className="text-gray-400">·</span>
                    <span className="text-xs text-gray-500">{o.driveMinutes} dk</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                ))}
              </div>
              <Link
                to="/karakopru-veteriner"
                className="text-sm font-semibold text-primary-700 hover:underline mt-4 inline-block"
              >
                Karaköprü’nün tüm mahalleleri →
              </Link>
            </div>
          </article>

          <aside className="space-y-6">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
              <h3 className="font-bold text-gray-900 mb-3">Klinik bilgileri</h3>
              <dl className="space-y-3 text-sm">
                <div>
                  <dt className="text-gray-500">Adres</dt>
                  <dd className="text-gray-800">{contactInfo.address}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">{n.name}’den mesafe</dt>
                  <dd className="text-gray-800 font-medium">{n.distanceKm} km · {n.driveMinutes} dk</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Hafta içi</dt>
                  <dd className="text-gray-800">{contactInfo.workingHours.weekdays}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Hafta sonu</dt>
                  <dd className="text-gray-800">{contactInfo.workingHours.weekend}</dd>
                </div>
              </dl>
              <a href={telHref} className="btn-primary w-full mt-5 inline-flex items-center justify-center gap-2">
                <Phone className="w-4 h-4" /> Hemen ara
              </a>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <h3 className="font-bold text-red-900">{n.name}’de acil durum</h3>
              </div>
              <p className="text-sm text-red-800 leading-relaxed">
                Zehirlenme, travma, doğum güçlüğü ve nefes darlığında beklemeyin.
                {n.name}’den {n.driveMinutes} dakikada buradasınız — yola çıkarken arayın,
                siz gelene kadar hazırlık yapalım.
              </p>
              <a href={telHref} className="text-sm font-semibold text-red-700 hover:underline mt-3 inline-block">
                {contactInfo.phone}
              </a>
            </div>
          </aside>
        </div>
      </section>
    </>
  )
}

export default NeighborhoodDetail
